import { useState, useEffect } from "react";
import Searching from "./components/Searching";
import DisplayData from "./components/DisplayData";
import DataList from "./components/DataList";
import LoadingSpinner from "./components/LoadingSpinner";
import Pagination from "./components/Pagination";
import ErrorDisplay from "./components/ErrorDisplay";
import LoginPage from "./components/LoginPage";
import SessionStatus from "./components/SessionStatus";
import useCustomers from "./hooks/useCustomers";
import AuthService from "./services/AuthService";

import "./App.css";

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = () => {
      setIsAuthenticated(AuthService.isAuthenticated());
      setIsCheckingAuth(false);
    };

    checkAuthStatus();

    // Set up automatic session extension if authenticated
    if (AuthService.isAuthenticated()) {
      AuthService.setupAutoSessionExtension();
    }
  }, []);

  const {
    customers,
    total,
    selectedCustomer,
    showModal,
    loading,
    error,
    handleSearch,
    nextPage,
    previousPage,
    openModal,
    closeModal,
    canGoNext,
    canGoPrevious,
    currentPage,
    hasToken,
  } = useCustomers();

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    // Set up automatic session extension
    AuthService.setupAutoSessionExtension();
  };

  const handleSessionExpired = () => {
    setIsAuthenticated(false);
    AuthService.logout();
  };

  const handleManualLogout = () => {
    AuthService.logout();
    setIsAuthenticated(false);
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return <LoadingSpinner size="large" text="Se verifica autentificarea..." />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Token validation
  if (!hasToken) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "40px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h1 style={{ color: "#dc3545" }}>Token lipsa!</h1>
          <p style={{ color: "#666" }}>
            Nu s-a gasit token-ul de autentificare in URL.
          </p>
        </div>
      </div>
    );
  }

  // Main loading state
  if (loading && customers.length === 0) {
    return <LoadingSpinner size="large" text="Se incarca datele..." />;
  }

  return (
    <div
      className="container-fluid"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        padding: "20px",
      }}
    >
      {/* Session Status */}
      <SessionStatus onSessionExpired={handleSessionExpired} />

      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h1
            style={{
              color: "#333",
              marginBottom: "10px",
              fontSize: "28px",
              margin: 0,
            }}
          >
            Gestiune de înregistrări
          </h1>
          <div style={{ color: "#666", fontSize: "16px" }}>
            {total > 0 ? (
              <>
                <span style={{ fontWeight: "500" }}>Total:</span> {total}{" "}
                înregistrări
                {customers.length > 0 && (
                  <>
                    {" | "}
                    <span style={{ fontWeight: "500" }}>Afișate:</span>{" "}
                    {customers.length}
                  </>
                )}
              </>
            ) : (
              <span style={{ fontStyle: "italic", color: "#999" }}>
                {loading ? "Se încarcă datele..." : "Nu există date de afișat"}
              </span>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleManualLogout}
          style={{
            padding: "8px 16px",
            backgroundColor: "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#c82333")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#dc3545")}
          title="Deconectare manuala"
        >
          Deconectare
        </button>
      </div>

      {/* Error Display */}
      <ErrorDisplay error={error} onRetry={() => handleSearch("")} />

      {/* Search */}
      <div
        style={{
          backgroundColor: "white",
          padding: "0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Searching search={handleSearch} isLoading={loading} />
      </div>

      {/* Pagination Top - Only show if there's data */}
      {total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onNext={nextPage}
          onPrevious={previousPage}
          canGoNext={canGoNext}
          canGoPrevious={canGoPrevious}
          isLoading={loading}
        />
      )}

      {/* Data List */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        <DataList data={customers} clicked={openModal} />
      </div>

      {/* Pagination Bottom - Only show if there's data */}
      {total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onNext={nextPage}
          onPrevious={previousPage}
          canGoNext={canGoNext}
          canGoPrevious={canGoPrevious}
          isLoading={loading}
        />
      )}

      {/* Modal */}
      {showModal && selectedCustomer && (
        <DisplayData
          customer={selectedCustomer}
          closeModal={closeModal}
          isOpen={showModal}
        />
      )}
    </div>
  );
};

export default App;
